<template>
  <Head title="Reports & Analytics" />

  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="space-y-6">
      <!-- Page Header with Info Button -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-secondary-900 dark:text-white">Reports & Analytics</h1>
          <p class="mt-1 text-secondary-600 dark:text-secondary-400">
            View comprehensive analytics across all modules
          </p>
        </div>
        <button
          @click="showInfo = true"
          class="p-2 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300 rounded-full hover:bg-secondary-100 dark:hover:bg-secondary-700"
          aria-label="Information"
        >
          <InformationCircleIcon class="h-6 w-6" />
        </button>
      </div>

      <!-- Date Range Selector -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg p-6 border border-secondary-200 dark:border-secondary-700">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="flex items-center space-x-4">
            <button
              v-for="range in dateRanges"
              :key="range.value"
              @click="changeDateRange(range.value)"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                selectedDateRange === range.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-600'
              ]"
            >
              {{ range.label }}
            </button>
          </div>

          <button
            @click="exportReport"
            class="flex items-center space-x-2 px-4 py-2 bg-success-600 text-white rounded-md hover:bg-success-700 transition-colors"
          >
            <ArrowDownTrayIcon class="h-5 w-5" />
            <span>Export</span>
          </button>
        </div>
      </div>

      <!-- Executive Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <SummaryCard
          title="Total Requests"
          :value="metrics.total_requests || 0"
          :change="metrics.requests_change"
          icon="ChartBarIcon"
          color="blue"
        />
        <SummaryCard
          title="Avg Resolution Time"
          :value="metrics.avg_resolution_time || 0"
          :change="metrics.resolution_time_change"
          icon="ClockIcon"
          suffix=" hrs"
          color="purple"
        />
        <SummaryCard
          title="SLA Compliance"
          :value="metrics.sla_compliance || 0"
          :change="metrics.sla_compliance_change"
          icon="CheckCircleIcon"
          suffix="%"
          color="green"
        />
        <SummaryCard
          title="Total Cost"
          :value="formatMoney(metrics.total_cost || 0)"
          :change="metrics.cost_change"
          icon="CurrencyDollarIcon"
          color="yellow"
        />
      </div>

      <!-- Module Breakdown -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
          <h3 class="text-lg leading-6 font-medium text-secondary-900 dark:text-white">
            Module Breakdown
          </h3>
        </div>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <!-- IT Helpdesk Module -->
            <div v-if="hasModule('it_helpdesk')" class="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <div class="flex items-center justify-between mb-4">
                <h4 class="text-lg font-semibold text-blue-900 dark:text-blue-100">IT Helpdesk</h4>
                <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
              </div>
              <div class="space-y-3">
                <div class="flex justify-between items-center">
                  <span class="text-sm text-blue-700 dark:text-blue-300">Total Tickets</span>
                  <span class="text-2xl font-bold text-blue-900 dark:text-blue-100">{{ moduleMetrics?.tickets?.count || 0 }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-blue-700 dark:text-blue-300">Avg Resolution</span>
                  <span class="text-lg font-semibold text-blue-900 dark:text-blue-100">{{ moduleMetrics?.tickets?.avg_resolution || 0 }} hrs</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-blue-700 dark:text-blue-300">Total Cost</span>
                  <span class="text-lg font-semibold text-blue-900 dark:text-blue-100">{{ formatMoney(moduleMetrics?.tickets?.cost || 0) }}</span>
                </div>
              </div>
            </div>

            <!-- Maintenance Module -->
            <div v-if="hasModule('maintenance')" class="bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-lg p-6">
              <div class="flex items-center justify-between mb-4">
                <h4 class="text-lg font-semibold text-green-900 dark:text-green-100">Maintenance</h4>
                <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div class="space-y-3">
                <div class="flex justify-between items-center">
                  <span class="text-sm text-green-700 dark:text-green-300">Work Orders</span>
                  <span class="text-2xl font-bold text-green-900 dark:text-green-100">{{ moduleMetrics?.work_orders?.count || 0 }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-green-700 dark:text-green-300">Avg Completion</span>
                  <span class="text-lg font-semibold text-green-900 dark:text-green-100">{{ moduleMetrics?.work_orders?.avg_completion || 0 }} hrs</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-green-700 dark:text-green-300">Total Cost</span>
                  <span class="text-lg font-semibold text-green-900 dark:text-green-100">{{ formatMoney(moduleMetrics?.work_orders?.cost || 0) }}</span>
                </div>
              </div>
            </div>

            <!-- Incidents Module -->
            <div v-if="hasModule('incidents')" class="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-6">
              <div class="flex items-center justify-between mb-4">
                <h4 class="text-lg font-semibold text-red-900 dark:text-red-100">Incidents</h4>
                <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div class="space-y-3">
                <div class="flex justify-between items-center">
                  <span class="text-sm text-red-700 dark:text-red-300">Total Incidents</span>
                  <span class="text-2xl font-bold text-red-900 dark:text-red-100">{{ moduleMetrics?.incidents?.count || 0 }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-red-700 dark:text-red-300">Avg Resolution</span>
                  <span class="text-lg font-semibold text-red-900 dark:text-red-100">{{ moduleMetrics?.incidents?.avg_resolution || 0 }} hrs</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-red-700 dark:text-red-300">Critical</span>
                  <span class="text-lg font-semibold text-red-900 dark:text-red-100">{{ moduleMetrics?.incidents?.critical || 0 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Requests Over Time -->
        <div class="bg-white dark:bg-secondary-800 shadow rounded-lg p-6 border border-secondary-200 dark:border-secondary-700">
          <h3 class="text-lg font-medium text-secondary-900 dark:text-white mb-4">
            Requests Over Time
          </h3>
          <LineChart :data="chartsData?.requests_over_time || {}" :height="300" />
        </div>

        <!-- Module Distribution -->
        <div class="bg-white dark:bg-secondary-800 shadow rounded-lg p-6 border border-secondary-200 dark:border-secondary-700">
          <h3 class="text-lg font-medium text-secondary-900 dark:text-white mb-4">
            Module Distribution
          </h3>
          <PieChart
            :data="moduleDistributionData"
            :colors="moduleDistributionColors"
            :height="300"
          />
        </div>

        <!-- Status Breakdown -->
        <div class="bg-white dark:bg-secondary-800 shadow rounded-lg p-6 border border-secondary-200 dark:border-secondary-700">
          <h3 class="text-lg font-medium text-secondary-900 dark:text-white mb-4">
            Status Breakdown
          </h3>
          <BarChart :data="chartsData?.status_breakdown || {}" :height="300" :stacked="false" />
        </div>

        <!-- SLA Compliance Trend -->
        <div class="bg-white dark:bg-secondary-800 shadow rounded-lg p-6 border border-secondary-200 dark:border-secondary-700">
          <h3 class="text-lg font-medium text-secondary-900 dark:text-white mb-4">
            SLA Compliance Trend
          </h3>
          <LineChart :data="chartsData?.sla_compliance_trend || {}" :height="300" />
        </div>
      </div>

      <!-- Team Performance -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
          <h3 class="text-lg leading-6 font-medium text-secondary-900 dark:text-white">
            Team Performance
          </h3>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
            <thead class="bg-secondary-50 dark:bg-secondary-900/50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                  Team Member
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                  Assigned
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                  Completed
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                  Avg Time
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-secondary-800 divide-y divide-secondary-200 dark:divide-secondary-700">
              <tr v-for="member in teamPerformance" :key="member.id">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-secondary-900 dark:text-white">
                    {{ member.name }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                  {{ member.assigned_count }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                  {{ member.completed_count }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                  {{ member.avg_completion_time }} hrs
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-1 bg-secondary-200 dark:bg-secondary-700 rounded-full h-2 mr-2">
                      <div
                        :class="[
                          'h-2 rounded-full transition-all',
                          member.performance >= 80 ? 'bg-success-500' :
                          member.performance >= 60 ? 'bg-warning-500' :
                          'bg-danger-500'
                        ]"
                        :style="{ width: member.performance + '%' }"
                      ></div>
                    </div>
                    <span class="text-sm text-secondary-700 dark:text-secondary-300">{{ member.performance }}%</span>
                  </div>
                </td>
              </tr>
              <tr v-if="!teamPerformance || teamPerformance.length === 0">
                <td colspan="5" class="px-6 py-8 text-center text-secondary-500 dark:text-secondary-400">
                  No team performance data available
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Info Modal -->
    <Modal :show="showInfo" @close="showInfo = false" title="Reports & Analytics" size="lg">
      <div class="space-y-4 text-sm text-secondary-600 dark:text-secondary-400">
        <p>
          <strong class="text-secondary-900 dark:text-white">What is this page?</strong><br>
          View comprehensive analytics and reports across all modules - tickets, work orders, and incidents.
        </p>
        <p>
          <strong class="text-secondary-900 dark:text-white">How to use:</strong><br>
          • Select a date range to view metrics for that period<br>
          • Review executive summary cards for quick insights<br>
          • Analyze charts to identify trends and patterns<br>
          • Export reports for presentations or record-keeping
        </p>
        <p>
          <strong class="text-secondary-900 dark:text-white">Key Metrics:</strong><br>
          • <strong>Total Requests:</strong> Combined count across all modules<br>
          • <strong>Avg Resolution Time:</strong> Average time to resolve across all types<br>
          • <strong>SLA Compliance:</strong> Percentage of requests resolved within SLA<br>
          • <strong>Total Cost:</strong> Combined maintenance and incident costs
        </p>
      </div>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, router } from '@inertiajs/vue3';
import route from '@/Utils/route';
import { useCurrency } from '@/Composables/useCurrency';
import SummaryCard from '@/Components/Reports/SummaryCard.vue';
import LineChart from '@/Components/Charts/LineChart.vue';
import PieChart from '@/Components/Charts/PieChart.vue';
import BarChart from '@/Components/Charts/BarChart.vue';
import Modal from '@/Components/Common/Modal.vue';
import {
  InformationCircleIcon,
  ArrowDownTrayIcon,
} from '@heroicons/vue/24/outline';

const { formatMoney } = useCurrency();

const props = defineProps({
  metrics: {
    type: Object,
    default: () => ({}),
  },
  moduleMetrics: {
    type: Object,
    default: () => ({}),
  },
  chartsData: {
    type: Object,
    default: () => ({}),
  },
  teamPerformance: {
    type: Array,
    default: () => [],
  },
  enabledModules: {
    type: Array,
    default: () => ['it_helpdesk', 'maintenance', 'incidents'],
  },
  dateRange: {
    type: String,
    default: '30',
  },
});

const showInfo = ref(false);
const selectedDateRange = ref(props.dateRange);

const dateRanges = [
  { label: 'Last 7 Days', value: '7' },
  { label: 'Last 30 Days', value: '30' },
  { label: 'Last 90 Days', value: '90' },
  { label: 'This Year', value: 'year' },
];

const hasModule = (module) => {
  return props.enabledModules?.includes(module) ?? true;
};

// Process module distribution data for pie chart
const moduleDistributionData = computed(() => {
  const rawData = props.chartsData?.module_distribution || [];

  // Transform to simple label/value format
  return rawData.map(item => ({
    label: item.label,
    value: item.value
  }));
});

const moduleDistributionColors = computed(() => {
  const rawData = props.chartsData?.module_distribution || [];
  return rawData.map(item => item.color);
});

const changeDateRange = (range) => {
  selectedDateRange.value = range;
  router.get('/reports', {
    range: range,
  }, {
    preserveScroll: true,
    only: ['metrics', 'moduleMetrics', 'chartsData', 'teamPerformance', 'dateRange'],
  });
};

const exportReport = () => {
  // Build URL with query parameters
  const params = {
    range: selectedDateRange.value,
  };

  const url = route('reports.export', params);

  // Open PDF in new tab
  window.open(url, '_blank');
};
</script>
